import React, { useEffect, useState } from "react";
import { Button, Dialog, EditIcon, PlusIcon, SearchInput, TrashIcon, Checkbox } from "evergreen-ui";
import { Table, Card, Select, Input, DatePicker, Form, message, Popconfirm } from "antd";
import { MDBAlert, MDBBox, MDBCol, MDBRow } from "mdbreact";
import { getDatabase, ref, update } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useList, useListVals } from "../../../Firebase/databaseHooks";
import { TableExport } from "tableexport";
import moment from "moment";

import Firebase from "../../../Firebase/Firebase";
import FireFetch from "../../../Firebase/FireFetch";
import CommunityUploadModal from "../../../Modals/UploadModals/CommunityUploadModal";
import TableActionMenu from "../../../Common/TableActionMenu";
import { isActiveUserStatus } from "../../../../constants/userStatus";
import { filterSelectOption, sortSelectOptions } from "../../../utils/selectOptions.js";

const { TextArea } = Input;
const latRegex = /^(-?[1-8]?\d(?:\.\d{1,8})?|90(?:\.0{1,8})?)$/;
const longRegex = /^(-?(?:1[0-7]|[1-9])?\d(?:\.\d{1,8})?|180(?:\.0{1,8})?)$/;

const db = getDatabase(Firebase);
const auth = getAuth(Firebase);

const CommunitiesLayout = (props) => {
	const canCreate = props.canCreate !== false;
	const canUpdate = props.canUpdate !== false;
	const canDelete = props.canDelete !== false;
	const [form] = Form.useForm();
	const columns = [
		{
			title: 'Community',
			dataIndex: 'community',
			key: 'community',
			sorter: {
				compare: (a, b) => a.community && a.community.localeCompare(b.community),
			},
		},
		{
			title: 'District',
			dataIndex: 'district',
			key: 'district',
			sorter: {
				compare: (a, b) => a.district && a.district.localeCompare(b.district),
			},
		},
		{
			title: 'Country',
			dataIndex: 'country',
			key: 'country',
			sorter: {
				compare: (a, b) => a.countryId && a.countryId.localeCompare(b.countryId),
			},
		},
		{
			title: 'Organization',
			dataIndex: 'org',
			key: 'org',
			sorter: {
				compare: (a, b) => a.orgID && a.orgID.localeCompare(b.orgID),
			},
		},
		{
			title: 'Responsible User',
			dataIndex: 'responsibleUser',
			key: 'responsibleUser',
			sorter: (a, b) => a.responsibleUser && a.responsibleUser.localeCompare(b.responsibleUser),
		},
		{
			title: 'Inactivation Date',
			dataIndex: 'inactivationDate',
			key: 'inactivationDate',
			sorter: (a, b) => a.inactivationDate && a.inactivationDate.localeCompare(b.inactivationDate),
		},
		{
			title: 'Start Date',
			dataIndex: 'date',
			key: 'date',
			sorter: (a, b) => a.date && a.date.localeCompare(b.date),
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
		}
	];

	const [communitiesList, setCommunitiesList] = useState([]);
	const [arrayIn, loading] = useList(ref(db, "talmidsdb/communities"));
	const [countries] = useListVals(ref(db, "talmidsdb/countries"));
	const [color, setColor] = useState("info");
	const [showAlert, setShowAlert] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [inDate, setInDate] = useState(null);
	const [userArray] = useListVals(ref(db, "talmidsdb/users"));
	const [orgArray] = useListVals(ref(db, "talmidsdb/organisations"));
	const [zones] = useListVals(ref(db, "talmidsdb/zones"));
	const [orgsList] = useListVals(ref(db, "talmidsdb/organisations"));
	const [districts] = useListVals(ref(db, "talmidsdb/district"));
	const [dataArray, setDataArray] = useState([]);
	const [startDate, setStartDate] = useState(null);
	const [community, setCommunity] = useState(null);
	const [uploadModal, setUploadModal] = useState(false);
	const [districtArray, setDistrictArray] = useState([]);

	const onChangeOne = (date, dateString) => {
		setStartDate(dateString);
	};

	const onChangeTwo = (date, dateString) => {
		setInDate(dateString);
	};

	const handleSort = (a, b) => {
		const compareCountry = a.countryId && a.countryId.localeCompare(b.countryId);
		const compareOrganization = a.orgID && a.orgID.localeCompare(b.orgID);
		const compareDistrict = a.district && a.district.localeCompare(b.district);
		const compareName = a.community && a.community.localeCompare(b.community);
		return compareCountry || compareOrganization || compareDistrict || compareName;
	};

	const getCommunityName = (communityID) => {
		const community = arrayIn?.find(x => x.communityID === communityID);
		return community?.name ?? '-';
	};

	const getCountryName = (countryID) => {
		const country = countries?.find(x => x.id === countryID);
		if (country?.countryName) return country.countryName;
		const raw = String(countryID ?? "");
		if (!raw) return "-";

		if (raw.includes("-")) {
			const suffix = raw.split("-").pop();
			const bySuffix = countries?.find((x) =>
				normalizeValue(x?.alpha3Code) === normalizeValue(suffix) ||
				normalizeValue(String(x?.id ?? "").split("-").pop()) === normalizeValue(suffix)
			);
			if (bySuffix?.countryName) return bySuffix.countryName;
		}

		const byAlpha = countries?.find((x) => normalizeValue(x?.alpha3Code) === normalizeValue(raw));
		if (byAlpha?.countryName) return byAlpha.countryName;

		const byIdSuffix = countries?.find((x) =>
			normalizeValue(String(x?.id ?? "").split("-").pop()) === normalizeValue(raw)
		);
		if (byIdSuffix?.countryName) return byIdSuffix.countryName;

		const byName = countries?.find((x) => normalizeValue(x?.countryName ?? x?.name) === normalizeValue(raw));
		return byName?.countryName ?? raw;
	};

	const getDistrictName = (districtID) => {
		const district = districts?.find(x => x.id === districtID || x.districtID === districtID);
		return district?.districtName ?? district?.district ?? "-";
	};

	const getOrgName = (orgID) => {
		const org = orgsList?.find(x => x.id === orgID || x.orgID === orgID);
		return org?.name ?? '-';
	};

	const getUserName = (userID) => {
		const user = userArray?.find(x => x.id === userID);
		return user ? `${user.firstname ?? ""} ${user.surname ?? ""}`.trim() : undefined;
	};

	const normalizeValue = (value) => String(value ?? "").trim().toLowerCase();

	const resolveCountryValue = (value) => {
		if (!value) return undefined;
		const raw = String(value);
		const byId = countries?.find((x) => x?.id === raw);
		if (byId?.id) return byId.id;

		if (raw.includes("-")) {
			const suffix = raw.split("-").pop();
			const bySuffix = countries?.find((x) =>
				normalizeValue(x?.alpha3Code) === normalizeValue(suffix) ||
				normalizeValue(String(x?.id ?? "").split("-").pop()) === normalizeValue(suffix)
			);
			if (bySuffix?.id) return bySuffix.id;
		}

		const byAlpha = countries?.find((x) => normalizeValue(x?.alpha3Code) === normalizeValue(raw));
		if (byAlpha?.id) return byAlpha.id;

		const byIdSuffix = countries?.find((x) =>
			normalizeValue(String(x?.id ?? "").split("-").pop()) === normalizeValue(raw)
		);
		if (byIdSuffix?.id) return byIdSuffix.id;

		const byName = countries?.find((x) => normalizeValue(x?.countryName ?? x?.name) === normalizeValue(raw));
		return byName?.id;
	};

	const resolveZoneValue = (value) => {
		if (!value) return undefined;
		const raw = String(value);
		const zone = zones?.find((x) =>
			x?.id === raw ||
			x?.zoneID === raw ||
			normalizeValue(x?.zone) === normalizeValue(raw)
		);
		return zone?.id ?? zone?.zoneID;
	};

	const resolveOrgValue = (value) => {
		if (!value) return undefined;
		const raw = String(value);
		const org = orgArray?.find((x) =>
			x?.id === raw ||
			x?.orgID === raw ||
			normalizeValue(x?.name) === normalizeValue(raw)
		);
		return org?.id ?? org?.orgID;
	};

	const resolveUserValue = (value) => {
		if (!value) return undefined;
		const raw = String(value);
		const user = userArray?.find((x) =>
			x?.id === raw ||
			normalizeValue(`${x?.firstname ?? ""} ${x?.surname ?? ""}`) === normalizeValue(raw)
		);
		return user?.id;
	};

	const resolveDistrictValue = (value) => {
		if (!value) return undefined;
		const raw = String(value);
		const district = districts?.find((x) =>
			x?.id === raw ||
			x?.districtID === raw ||
			normalizeValue(x?.districtName) === normalizeValue(raw) ||
			normalizeValue(x?.district) === normalizeValue(raw)
		);
		return district?.id ?? district?.districtID;
	};

	useEffect(() => {
		setCommunitiesList([]);
		setDataArray([]);
		const tempArray = [];

		onAuthStateChanged(auth, (user) => {
			if (user) {
				if (arrayIn) {
					const use = userArray[userArray.findIndex(x => (user.uid) === x.id)] && userArray[userArray.findIndex(x => (user.uid) === x.id)];
					const role = use && use.userRole;

					arrayIn.forEach((snap, index) => {
						const item = snap.val();
						tempArray.push({
							key: index,
							community: item?.name,
							communityId: getCommunityName(item?.districtID),
							country: getCountryName(item?.countryID),
							district: getDistrictName(item?.districtID ?? item?.district),
							org: getOrgName(item?.orgID ?? item?.organisationID),
							responsibleUser: getUserName(item?.responsibleUserID ?? item?.responsibleUser),
							date: item?.startDate,
							action: (
								<TableActionMenu
									items={[
										canUpdate && {
											key: "edit",
											label: "Edit",
											onClick: () => {
												setCommunity(item);
												setShowModal(true);
											},
										},
										canDelete && {
											key: "delete",
											label: "Delete",
											danger: true,
											onClick: () => {
												if (confirm("Delete this community?")) {
													props.messageBack("communities", "delete", item?.id ?? item?.communityID);
												}
											},
										},
									]}
								/>
							)
						});
					});

					const sortedArray = tempArray.sort(handleSort);
					setCommunitiesList([...sortedArray]);
					setDataArray([...sortedArray]);
				}
			}
		});

		if (districts) {
			setDistrictArray(districts);
		}
	}, [arrayIn, countries, districts, orgsList, props, userArray, canUpdate, canDelete]);

	useEffect(() => {
		if (!showModal || !community) {
			return;
		}

		const countryValue = resolveCountryValue(community?.countryID ?? community?.countryId);
		const zoneValue = resolveZoneValue(community?.zoneID ?? community?.zone);
		const orgValue = resolveOrgValue(community?.orgID ?? community?.organisationID ?? community?.organisation);
		const districtValue = resolveDistrictValue(community?.districtID ?? community?.district);
		const userValue = resolveUserValue(community?.responsibleUserID ?? community?.responsibleUser);

		if (countryValue) {
			countryChange(countryValue);
		} else {
			setDistrictArray(districts ?? []);
		}

		form.setFieldsValue({
			name: community?.name ?? "",
			zoneID: zoneValue,
			countryID: countryValue,
			responsibleUserID: userValue,
			orgID: orgValue,
			districtID: districtValue,
			advanced: Boolean(community?.advanced),
			notes: community?.notes ?? "",
			salesforceID: community?.salesforceID ?? "",
			startDate: community?.startDate ? moment(community.startDate, "YYYY-MM-DD") : undefined,
			inactivationDate: community?.inactivationDate ? moment(community.inactivationDate, "YYYY-MM-DD") : undefined,
			latitude: community?.latitude ?? "",
			longitude: community?.longitude ?? "",
		});
	}, [showModal, community, form, countries, zones, orgArray, userArray, districts]);

	const handleSearch = (searchText) => {
		searchText = searchText.toLowerCase();
		const filteredEvents = dataArray.filter(({ community, countryId, district, date, orgID, responsibleUser }) => {
			countryId = countryId && countryId.toLowerCase();
			community = community && community.toLowerCase();
			responsibleUser = responsibleUser && responsibleUser.toLowerCase();
			orgID = orgID && orgID.toLowerCase();
			date = date && date.toLowerCase();
			district = district && district.toLowerCase();
			return (countryId && countryId.includes(searchText)) ||
				(community && community.includes(searchText)) ||
				(responsibleUser && responsibleUser.includes(searchText)) ||
				(date && date.includes(searchText)) ||
				(district && district.includes(searchText)) ||
				(orgID && orgID.includes(searchText));
		});

		setCommunitiesList(filteredEvents);
	};

	const countryChange = (option) => {
		const selectedCountry = countries?.find((x) => x?.id === option);
		const codeCandidates = new Set();
		const addCode = (value) => {
			const normalized = String(value ?? "").trim().toUpperCase();
			if (normalized) codeCandidates.add(normalized);
		};

		addCode(selectedCountry?.alpha3Code);
		if (typeof selectedCountry?.id === "string" && selectedCountry.id.includes("-")) {
			addCode(selectedCountry.id.split("-").pop());
		}
		if (typeof option === "string" && option.includes("-")) {
			addCode(option.split("-").pop());
		}

		if (codeCandidates.has("ZMB")) codeCandidates.add("ZAM");
		if (codeCandidates.has("ZAM")) codeCandidates.add("ZMB");

		const filterArray = districts?.filter((district) => {
			const province = String(district?.province ?? district?.provinceID ?? "").toUpperCase();
			const countryID = String(district?.countryID ?? "");
			return (selectedCountry?.id && countryID === selectedCountry.id) ||
				Array.from(codeCandidates).some((code) => province.includes(code));
		});
		setDistrictArray(filterArray?.length ? filterArray : (districts ?? []));
	};

	const handleUpdate = (values) => {
		if (!canUpdate) {
			message.error("You do not have permission to update communities.");
			return;
		}

		const object = {
			name: values.name ?? "",
			countryID: values.countryID ?? "",
			startDate: values.startDate ? values.startDate.format("YYYY-MM-DD") : "",
			inactivationDate: values.inactivationDate ? values.inactivationDate.format("YYYY-MM-DD") : "",
			orgID: values.orgID ?? "",
			responsibleUserID: values.responsibleUserID ?? "",
			districtID: values.districtID ?? "",
			zoneID: values.zoneID ?? "",
			advanced: values.advanced ?? false,
			notes: values.notes ?? "",
			salesforceID: values.salesforceID ?? "",
			latitude: values.latitude ?? "",
			longitude: values.longitude ?? "",
		};

		const communityId = community?.id ?? community?.communityID;
		if (!communityId) {
			message.error("Unable to update community: missing community ID.");
			return;
		}

		update(ref(db, "talmidsdb/communities/" + communityId), object)
			.then(() => {
				message.success("Community updated successfully");
				FireFetch.addLog("Communities", communityId, "Edit");
				setShowModal(false);
			})
			.catch((error) => {
				message.error("Unable to update Community due to an error: " + error.message);
			});
	};

	return (
		<div className="font-Nunito">
			<Dialog
				isShown={showModal}
				title={"Edit Community"}
				onCloseComplete={() => {
					setShowModal(false);
					setCommunity(null);
					form.resetFields();
				}}
				hasFooter={false}
			>
				<MDBBox>
					<MDBRow>
						<MDBCol md={12} className="d-flex flex-column">
							{showAlert && (
								<MDBAlert color={color} className="my-3">
									{errorMessage}
								</MDBAlert>
							)}
							<Form
								form={form}
								className="px-4"
								layout="vertical"
								onFinish={handleUpdate}
							>
								<Form.Item
									className="d-flex flex-column my-2"
									label={"Enter Community name"}
									name={"name"}
									rules={[{ message: 'Please input name!' }]}
								>
									<Input
										type="text"
										placeholder="enter Community name"
										className="w-100 mr-4"
										id="name"
									/>
								</Form.Item>

								<Form.Item
									label={"Enter Community's country"}
									name="countryID"
									rules={[{
										message: 'Please input community country!'
									}]}
								>
									<Select
										placeholder="select country of Community"
										showSearch
										name="countryID"
										optionFilterProp="children"
										filterOption={filterSelectOption}
										filterSort={sortSelectOptions}
										onChange={countryChange}
									>
										{countries?.map((item, index) => (
											<Select.Option value={item?.id} key={index}>
												{item?.countryName}
											</Select.Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item
									label={"Select Community's zone"}
									name="zoneID"
									rules={[{
										message: 'Please input community zone!'
									}]}
								>
									<Select
										placeholder="select zone of Community"
										showSearch
										optionFilterProp="children"
										filterOption={filterSelectOption}
										filterSort={sortSelectOptions}
									>
										{zones?.map((item, index) => (
											<Select.Option key={index} value={item?.id ?? item?.zoneID}>
												{item?.zone ?? item?.name}
											</Select.Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item
									label={"Enter Community's organisation"}
									name="orgID"
									rules={[{
										message: 'Please input community organisation!'
									}]}
								>
									<Select
										placeholder="Select Organization"
										showSearch
										optionFilterProp="children"
										filterOption={filterSelectOption}
										filterSort={sortSelectOptions}
									>
										{orgArray?.map((item, index) => (
											<Select.Option value={item?.id ?? item?.orgID} key={index}>
												{item?.name}
											</Select.Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item
									label="Select community's responsible user"
									name="responsibleUserID"
								>
									<Select
										placeholder="Select responsible User"
										showSearch
										optionFilterProp="children"
										filterOption={filterSelectOption}
										filterSort={sortSelectOptions}
									>
										{userArray?.filter((x) => isActiveUserStatus(x.userStatus)).map((item, index) => (
											<Select.Option value={item?.id} key={index}>
												{item?.firstname + " " + item?.surname}
											</Select.Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item
									label="Select district"
									name="districtID"
								>
									<Select
										placeholder="Select District"
										showSearch
										optionFilterProp="children"
										filterOption={filterSelectOption}
										filterSort={sortSelectOptions}
									>
										{districtArray?.map((item, index) => (
											<Select.Option value={item?.id ?? item?.districtID} key={index}>
												{item?.districtName ?? item?.district}
											</Select.Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item
									label="Latitude"
									name="latitude"
									rules={[
										{
											pattern: new RegExp(latRegex),
											message: "Wrong latitude format!"
										}
									]}
								>
									<Input
										type="number"
										placeholder="enter latitude"
										id="lat"
									/>
								</Form.Item>

								<Form.Item
									label="Longitude"
									name="longitude"
									rules={[
										{
											pattern: new RegExp(longRegex),
											message: "Wrong longitude format!"
										}
									]}
								>
									<Input
										type="number"
										placeholder="enter longitude"
										id="long"
									/>
								</Form.Item>

								<Form.Item
									label="Select starting date"
									name="startDate"
									rules={[{
										required: true,
										message: 'Please input start date!'
									}]}
								>
									<DatePicker
										placeholder="select starting date"
										picker={"date"}
										className="w-100"
										onChange={onChangeOne}
									/>
								</Form.Item>

								<Form.Item
									label="Select inactivation date"
									name="inactivationDate"
								>
									<DatePicker
										placeholder="select inactivation date"
										picker={"date"}
										className="w-100"
										onChange={onChangeTwo}
									/>
								</Form.Item>

								<Form.Item
									label="Advanced"
									name="advanced"
									valuePropName="checked"
								>
									<Checkbox label="Advanced" />
								</Form.Item>

								<Form.Item
									label="Enter notes"
									name="notes"
								>
									<TextArea
										type="text"
										placeholder="enter notes"
										className="w-100 mr-4"
										id="notes"
									/>
								</Form.Item>

								<Form.Item
									label="Enter community SalesForce ID"
									name="salesforceID"
								>
									<Input
										type="text"
										placeholder="enter Community SalesForce ID"
										className="w-100 mr-4"
										id="salesforce"
									/>
								</Form.Item>

								<Button appearance="primary" htmlType="submit" className="mt-3">
									Update
								</Button>
							</Form>
						</MDBCol>
					</MDBRow>
				</MDBBox>
			</Dialog>

			<CommunityUploadModal
				showModal={uploadModal}
				setShowModal={setUploadModal}
				name={"Communities"}
				id={"community"}
			/>

			<div className="d-flex flex-row align-items-center">
				<SearchInput placeholder="Filter communities" onChange={e => handleSearch(e.target.value)} />
				{canCreate && (
					<Button
						appearance="primary"
						className="mx-1 my-2"
						onClick={() => {
							props.messageBack("communities", "create");
						}}
					>
						New Community <PlusIcon className="ml-2" />
					</Button>
				)}
				<Button
					appearance="primary"
					className="mx-1 my-2"
					onClick={() => {
						FireFetch.exportXL(TableExport(document.getElementById("dataTable"), {
							filename: "Communities",
							exportButtons: false,
							sheetname: "Communities",
						}), communitiesList);
					}}
				>
					Print
				</Button>
				{canCreate && (
					<Button
						className="mx-1 my-2"
						onClick={() => { setUploadModal(!uploadModal) }}
					>
						Upload
					</Button>
				)}
			</div>

			<Card className="d-flex flex-column">
				<div>
					<Table
						style={{ overflow: "auto" }}
						loading={loading}
						id={"dataTable"}
						pagination={{
							defaultPageSize: 100,
							showSizeChanger: true,
							pageSizeOptions: ['20', '50', '100']
						}}
						dataSource={communitiesList}
						columns={columns}
					/>
				</div>
			</Card>
		</div>
	);
};

export default CommunitiesLayout;
